import { a3Copy } from "@/components/assignments/a3-copy";
/**
 * What a learner handed in, read wherever the attempt is: the learner's own
 * hand-in, the teacher's review, the gradebook and the archived record. One
 * renderer, so a link reads the same, opens the same way and states the same
 * access rule in every place; a private file opens through the same
 * authorized, short-lived grant with the same refusals.
 */
import { useState } from "react";
import { Download } from "lucide-react";

import { DocumentReadOnly } from "@/components/assignments/AssignmentDocument";
import { Button } from "@/components/ui/button";
import { assignmentsApi, type Submission } from "@/lib/api/assignments";
import { ApiError } from "@/lib/api/client";
import { SUBMISSION_KIND_LABEL } from "@/lib/assignment-presentation";
import { safeHref } from "@/lib/safe-href";

export function AttemptContent({ attempt, expanded = false }: { attempt: Submission; expanded?: boolean }) {
  if (attempt.content_kind === "link" && attempt.link_url) return <HandedInLink url={attempt.link_url} />;
  if (attempt.content_kind === "text") {
    // max-w-full, not a fixed cap: the cell is the bound now, and `truncate`
    // only ellipsises against a constrained parent.
    return attempt.document_snapshot && expanded
      ? <DocumentReadOnly document={attempt.document_snapshot} />
      : attempt.document_snapshot
        // "Written response" is what the app calls a text attempt everywhere else (SUBMISSION_KIND_LABEL).
        ? <details className="grid gap-2"><summary className="cursor-pointer text-ui font-medium">{SUBMISSION_KIND_LABEL.text}</summary><DocumentReadOnly document={attempt.document_snapshot} /></details>
        : <p dir="auto" className="max-w-full whitespace-pre-wrap text-ui text-foreground [overflow-wrap:anywhere]">{attempt.content_text}</p>;
  }
  if (attempt.artifact) return <PrivateFile key={attempt.artifact.id} artifact={attempt.artifact} />;
  return (
    <span className="text-ui text-muted-foreground">
      {SUBMISSION_KIND_LABEL[attempt.content_kind]}
    </span>
  );
}

/**
 * The address itself is the link text: the reader sees which host they are
 * about to visit, and that host, not IMAM, decides who may open it. A stored
 * value that is not a web address is shown as text and never becomes a link.
 */
function HandedInLink({ url }: { url: string }) {
  const href = safeHref(url);
  return <div data-attempt-link className="grid min-w-0 gap-1">
    {href
      ? <a href={href} target="_blank" rel="noopener noreferrer" dir="ltr" className="justify-self-start text-ui font-medium text-primary underline-offset-4 [overflow-wrap:anywhere] hover:underline">{href}<span className="sr-only">{a3Copy.opensInANewTab}</span></a>
      : <p dir="ltr" className="text-ui text-foreground [overflow-wrap:anywhere]">{url}</p>}
    <p className="text-meta text-muted-foreground">{href ? a3Copy.theLinkSHostDecidesWhoCanOpenIt : a3Copy.thisIsNotAWebAddressSoItCannotBeOpened}</p>
  </div>;
}

function PrivateFile({ artifact }: { artifact: NonNullable<Submission["artifact"]> }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The file is named by its own name, as a link is by its address.
  return <div className="grid min-w-0 justify-items-start gap-1">
    <Button type="button" size="sm" variant="subtle" className="-ms-2.5 h-auto min-h-11 max-w-full whitespace-normal text-start [overflow-wrap:anywhere]" disabled={busy} onClick={async () => {
      setError(null);
      // The short-lived grant opens in its own tab, claimed before the request
      // so the click still counts as the gesture. Whatever the storage answers,
      // this page and any unsaved writing or judgement on it stay put.
      const fileWindow = window.open("about:blank", "_blank");
      if (!fileWindow) { setError(a3Copy.allowANewTabToOpenThisFileThenTryAgain); return; }
      fileWindow.opener = null;
      setBusy(true);
      try {
        fileWindow.location.replace((await assignmentsApi.downloadArtifact(artifact.id)).download_url);
      } catch (error) {
        fileWindow.close();
        setError(error instanceof ApiError
          ? error.status === 410 ? a3Copy.thisFileIsNoLongerAvailable : error.message
          : a3Copy.couldNotPrepareTheDownload);
      } finally { setBusy(false); }
    }}><Download aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />{artifact.original_name ?? a3Copy.uploadedFile}</Button>
    {error && <p role="alert" className="text-ui">{error}</p>}
  </div>;
}
